import { type Client, createClient } from 'graphql-ws';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getRelayExperimentalApis } from '@atlassian/relay-experimental-apis';

import {
	WS_CLIENT_CAPPED_RETRIES,
	WS_CLIENT_CONNECTION_ACK_WAIT,
	WS_CLIENT_KEEP_ALIVE,
	WS_CLIENT_LAZY_CLOSE,
	WS_CLIENT_LIVENESS_KEEP_ALIVE,
	WS_CLIENT_MAX_RETRY_WAIT,
	WS_CLIENT_PONG_WAIT,
	WS_CLIENT_RETRIES,
} from '../../constants';
import { getAggSubscriptionEndpoint, isSSR } from '../index';
import { addRecoveryListeners, createRetryWait, shouldRetryTransportFailure } from './recovery';
import { createSocketListeners, type WsConnectionEvent } from './socketListeners';

export type { WsConnectionEvent } from './socketListeners';

/**
 * WebSocket client configuration. Only where to report the connection lifecycle is left to the
 * consumer, because an analytics client is product-specific; the transport itself is decided in
 * this package so that every product behaves the same way.
 */
export type WsClientOptions = {
	/**
	 * Returns the function to call for every connection-lifecycle event, or undefined to report
	 * nothing. A getter rather than the callback itself, so declining attaches no listeners at
	 * all - a callback that discarded the events would still cost the counters and the error
	 * classification needed to build them.
	 */
	getConnectionEventReporter?: () => ((event: WsConnectionEvent) => void) | undefined;
};

let client: Client | null;
let configuredOptions: WsClientOptions | undefined;
// Set when the client is built, so `attachConnectionRecovery` can read the socket state from
// outside `getClient`.
let isSocketConnected: () => boolean = () => false;
let recoveryCount = 0;
let detachRecovery: (() => void) | undefined;

/**
 * Configure the WebSocket client before it is created. `createRelayEnvironment` calls this when
 * passed `wsClientOptions`.
 *
 * The client is a module singleton shared by every subscription on the page, so configuration is
 * page-scoped and set once, up front. `graphql-ws` captures its options at construction, so a
 * later call cannot take effect and warns instead.
 */
export const configureWebSocketClient = (options: WsClientOptions): void => {
	if (client) {
		// eslint-disable-next-line no-console
		console.warn(
			'configureWebSocketClient was called after the subscription client was already created, so these options have been ignored. Configure it while creating the relay environment, before any subscription starts.',
		);
		return;
	}
	configuredOptions = options;
};

export const getClient = (): Client | null => {
	if (isSSR()) {
		return null;
	}

	if (!client) {
		const onConnectionEvent = configuredOptions?.getConnectionEventReporter?.();
		// Read at call time, because the client is still being assigned here.
		const terminate = () => client?.terminate();
		const { listeners, isConnected } = createSocketListeners({
			onConnectionEvent,
			pongWaitTimeout: fg('platform_relay_subscription_reliability') ? WS_CLIENT_PONG_WAIT : 0,
			terminate,
		});
		isSocketConnected = isConnected;
		client = createClient({
			url: getAggSubscriptionEndpoint(),
			lazy: true,
			keepAlive: fg('platform_relay_subscription_reliability')
				? WS_CLIENT_LIVENESS_KEEP_ALIVE
				: WS_CLIENT_KEEP_ALIVE,
			retryAttempts: fg('platform_relay_subscription_reliability')
				? WS_CLIENT_CAPPED_RETRIES
				: WS_CLIENT_RETRIES,
			// Undefined leaves the library's own uncapped backoff and `isLikeCloseEvent` in place.
			retryWait: fg('platform_relay_subscription_reliability')
				? createRetryWait(WS_CLIENT_MAX_RETRY_WAIT)
				: undefined,
			shouldRetry: fg('platform_relay_subscription_reliability')
				? shouldRetryTransportFailure
				: undefined,
			// Zero is the library's own value for both: wait indefinitely, and close as soon as the
			// last subscription releases the socket.
			connectionAckWaitTimeout: fg('platform_relay_subscription_reliability')
				? WS_CLIENT_CONNECTION_ACK_WAIT
				: 0,
			lazyCloseTimeout: fg('platform_relay_subscription_connection_reuse')
				? WS_CLIENT_LAZY_CLOSE
				: 0,
			// The deadline and the connection flag both need the socket events.
			on:
				onConnectionEvent || fg('platform_relay_subscription_reliability') ? listeners : undefined,
			connectionParams: {
				'atl-client-name': 'jira-frontend',
				'atl-client-version': `${(window as any)?.BUILD_KEY ?? ''}::${(window as any)?.SSR_BUILD_KEY ?? ''}`,
				'X-ExperimentalApi': getRelayExperimentalApis().join(','),
			},
		});
	}
	return client;
};

/**
 * Attaches the browser signals that force a reconnect - the network returning, a tab becoming
 * visible, a back/forward-cache restore, a resume from a frozen tab - and returns the function
 * that detaches them.
 *
 * Deliberately not done by `getClient`, so that building the client registers nothing on the
 * page, and so the caller that knows its own lifecycle owns theirs. Consumers are expected to
 * call this once where their app mounts.
 *
 * Reference-counted, because a page can hold more than one caller: the listeners go on with the
 * first and come off with the last, so one caller unmounting cannot strip recovery from the
 * rest. The returned function is safe to call more than once, which a React effect cleanup will
 * do under StrictMode.
 *
 * Safe to call before anything has subscribed - with no client yet, the listeners do nothing.
 */
export const attachConnectionRecovery = (): (() => void) => {
	if (!fg('platform_relay_subscription_reliability')) {
		return () => {};
	}

	recoveryCount += 1;
	detachRecovery ??= addRecoveryListeners({
		terminate: () => client?.terminate(),
		isConnected: () => isSocketConnected(),
	});

	let released = false;
	return () => {
		if (released) {
			return;
		}
		released = true;
		recoveryCount -= 1;
		if (recoveryCount === 0) {
			detachRecovery?.();
			detachRecovery = undefined;
		}
	};
};

export const cleanUpClient = (): void => {
	client = null;
	configuredOptions = undefined;
	isSocketConnected = () => false;
};
